import { createContext, useContext } from 'react';

const MIN_PASSWORD_LENGTH = 8;

export type AuthContextType = {
  currentUser: { username: string; userId: string; attributes?: Record<string, string> } | null;
  debugTokens: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  groups: string[];
  loading: boolean;
  logout: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};

export const hasNoEdgeSpaces = (password: string): boolean => {
  return password.startsWith(' ') === false && password.endsWith(' ') === false;
};

export const isPasswordFormatValid = (password: string): boolean => {
  return hasNoEdgeSpaces(password) &&
    isMinLength(password) &&
    containsUppercaseLetter(password) &&
    containsLowercaseLetter(password) &&
    containsNumber(password) &&
    containsSpecialCharacter(password);
};

export const isMinLength = (str: string): boolean => {
  return str.length >= MIN_PASSWORD_LENGTH;
};

export const containsSpecialCharacter = (str: string): boolean => {
  const specialCharRegex = /[?=.*[^$*.[\]{}()?"!@#%&/\\, ><':;|_~`=+-]/;
  return specialCharRegex.test(str);
};

export const containsNumber = (str: string): boolean => {
  const numberRegex = /[0-9]/;
  return numberRegex.test(str);
};

export function containsUppercaseLetter(str: string): boolean {
  const uppercaseRegex = /[A-Z]/;
  return uppercaseRegex.test(str);
}

export function containsLowercaseLetter(str: string): boolean {
  const lowercaseRegex = /[a-z]/;
  return lowercaseRegex.test(str);
}

export const isEmailFormatValid = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const PROPRIO_EMPLOYEES_GROUP = 'ProprioEmployees';

export const isProprioEmployee = (groups: string[]): boolean => {
  return groups.includes(PROPRIO_EMPLOYEES_GROUP);
};
